import { isPlatformBrowser } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  PLATFORM_ID,
  TemplateRef,
  inject,
} from '@angular/core';
import type { PermissionStatus, Photo } from '@capacitor/camera';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import {
  BrowserPermissionResultStatus,
  BrowserPermissions,
  BrowserPermissionStates,
} from '@freelancer/browser-permissions';
import { FileRead } from '@freelancer/file-read';
import {
  MediaDeviceErrorCode,
  MediaDeviceResultStatus,
  MediaDevices,
} from '@freelancer/media-devices';
import { NativeSettingsService } from '@freelancer/native-settings';
import { Pwa } from '@freelancer/pwa';
import { ButtonComponent } from '@freelancer/ui/button';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconModule } from '@freelancer/ui/icon';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import type { DynamicToastAlertOptions } from '@freelancer/ui/toast-alert';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import { Subject } from 'rxjs';
import { ModalSize } from '../modal/modal-size';
import { ModalService } from '../modal/modal.service';
import { permissionReason, Permissions, PermissionType } from '../permissions';
import {
  CameraImageType,
  CameraInputTriggerType,
  DEFAULT_VIDEO_CONSTRAINTS,
} from './camera-input.types';

@Component({
  standalone: true,
  selector: 'fl-camera-input',
  imports: [ButtonComponent, IconModule],
  template: `
    @if (triggerType === CameraInputTriggerType.DEFAULT) {
      <fl-button
        class="PhotoBtn"
        [attr.data-button-width-auto]="buttonWidthAuto"
        [color]="ButtonColor.DEFAULT"
        [size]="ButtonSize.SMALL"
        [disabled]="disabled"
        (click)="initTakePhoto()"
      >
        <fl-icon
          [name]="'ui-camera-outline'"
          [size]="IconSize.MID"
        ></fl-icon>
        @if (takePhotoCTA) {
          {{ takePhotoCTA }}
        }
        @if (!takePhotoCTA) {
          <ng-container i18n="Take a photo button"> Take Photo </ng-container>
        }
      </fl-button>
    }

    @if (triggerType === CameraInputTriggerType.FLOATING_ACTION_BUTTON) {
      <fl-button
        class="PhotoFloatingBtn"
        [size]="ButtonSize.XXLARGE"
        [disabled]="disabled"
        (click)="initTakePhoto()"
      >
        <fl-icon
          [color]="disabled ? IconColor.FOREGROUND : IconColor.SECONDARY"
          [name]="'ui-camera-outline'"
          [size]="IconSize.LARGE"
        ></fl-icon>
      </fl-button>
    }
  `,
  styleUrls: ['./camera-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CameraInputComponent implements OnInit {
  private browserPermission = inject(BrowserPermissions);
  private fileRead = inject(FileRead);
  private mediaDevices = inject(MediaDevices);
  private modalService = inject(ModalService);
  private permissions = inject(Permissions);
  private pwa = inject(Pwa);
  private nativeSettings = inject(NativeSettingsService);
  private toastAlertService = inject(ToastAlertService);
  private platformId = inject(PLATFORM_ID);

  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CameraInputTriggerType = CameraInputTriggerType;
  IconColor = IconColor;
  IconSize = IconSize;

  @Input() captureTimeout = 5; // in seconds
  @Input() buttonWidthAuto = true;
  @Input() disabled = false;
  @Input() modalHeader?: string;
  @Input() modalTitle?: string;
  @Input() takePhotoCTA?: string;
  @Input() uploadCTA?: string;
  @Input() videoConstraints: MediaTrackConstraints = {};
  @Input() imageType?: CameraImageType;

  @HostBinding('attr.data-trigger-type')
  @Input()
  triggerType = CameraInputTriggerType.DEFAULT;

  @Output() canActivate = new EventEmitter<boolean>();
  @Output() onError = new EventEmitter<MediaDeviceErrorCode>();
  @Output() takePhoto = new EventEmitter<File>();
  @Output() cameraNeedsAccess = new EventEmitter<boolean>();

  hasVideoDevices = false;

  @ContentChild('content') content: TemplateRef<CameraInputComponent>;

  private videoDevices: readonly MediaDeviceInfo[] = [];
  private videoDeviceConstraints: MediaTrackConstraints;
  private cameraFileSubject$ = new Subject<File>();

  cameraFile$ = this.cameraFileSubject$.asObservable();

  mediaDeviceErrorCode: MediaDeviceErrorCode | null;

  ngOnInit(): void {
    this.videoDeviceConstraints = {
      ...DEFAULT_VIDEO_CONSTRAINTS,
      ...this.videoConstraints,
    };
    this.setVideoDevices();
  }

  private setVideoDevices(): void {
    if (this.pwa.isNative()) {
      this.handleCameraActivate(true);
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      this.mediaDevices
        .getVideoDevices()
        .then(result => {
          if (result.status === MediaDeviceResultStatus.SUCCESS) {
            this.videoDevices = result.devices;
            this.hasVideoDevices = result.devices.length > 0;
            this.handleCameraActivate(this.hasVideoDevices);
          }
        })
        .catch(() => {
          this.hasVideoDevices = false;
          this.handleCameraActivate(false);
        });
    }
  }

  initTakePhoto(): void {
    if (this.pwa.isNative()) {
      this.capacitorTakePicture();
    } else {
      this.showModal();
    }
  }

  async showModal(): Promise<false | undefined> {
    if (!this.hasVideoDevices || this.disabled) {
      return false;
    }
    if (
      this.mediaDeviceErrorCode &&
      (this.mediaDeviceErrorCode ===
        MediaDeviceErrorCode.MEDIA_DEVICE_API_NOT_SUPPORTED ||
        this.mediaDeviceErrorCode === MediaDeviceErrorCode.UNSUPPORTED_BROWSER)
    ) {
      this.showError(this.mediaDeviceErrorCode);
      return false;
    }

    let browserPermissionModalVisibile = false;
    const device = this.videoDevices[0];
    const videoConstraint = {
      video: {
        deviceId: device.deviceId,
      },
    };

    const permission = await this.permissions.requestPermissions({
      permission: PermissionType.CAMERA,
      reason: permissionReason.CAMERA,
    });

    if (permission) {
      this.showBrowserPermissionModal()
        .then(result => {
          browserPermissionModalVisibile = result;
          return this.mediaDevices.getUserMedia(videoConstraint);
        })
        .then(result => {
          if (result.status === MediaDeviceResultStatus.SUCCESS) {
            return this.mediaDevices.stopMediaStream(result.stream);
          }
          this.showError(result.error);
          throw new Error(result.error);
        })
        .then(() => {
          if (!browserPermissionModalVisibile) {
            this.handleCameraModal();
          }
        })
        .catch(error => {
          this.handleOnError(error);
        });
    }
  }

  private handleCameraActivate(canActivate: boolean): void {
    this.canActivate.emit(canActivate);
  }

  private handleCameraModal(): void {
    this.modalService
      .open('CameraModal', {
        inputs: {
          content: this.content,
          captureTimeout: this.captureTimeout,
          imageType: this.imageType,
          modalHeader: this.modalHeader,
          modalTitle: this.modalTitle,
          uploadCTA: this.uploadCTA,
          videoDevices: this.videoDevices,
          videoConstraints: this.videoDeviceConstraints,
        },
        mobileFullscreen: true,
        size: ModalSize.MID,
      })
      .afterClosed()
      .then(result => {
        if (result) {
          this.takePhoto.emit(this.fileRead.readImageDataIntoFile(result));
        }
      });
  }

  private showError(error: MediaDeviceErrorCode): void {
    this.mediaDeviceErrorCode = error;
    this.toggleAccessDeniedBanner(true);
  }

  private handleOnError(error: MediaDeviceErrorCode): void {
    this.onError.emit(error);
  }

  private showBrowserPermissionModal(): Promise<boolean> {
    return this.browserPermission
      .getCameraPermissions()
      .then(result => {
        if (
          result.status === BrowserPermissionResultStatus.SUCCESS &&
          result.permissionStatus.state === BrowserPermissionStates.PROMPT
        ) {
          const permission = result.permissionStatus;
          permission.onchange = () => {
            if (permission.state === BrowserPermissionStates.GRANTED) {
              this.handleCameraModal();
            } else {
              this.modalService.close();
            }
          };
          return true;
        }
        return false;
      })
      .catch(() => false);
  }

  private async capacitorTakePicture(): Promise<void> {
    const cameraOptions = {
      quality: 90,
      allowEditing: false,
      source: CameraSource.Camera,
      resultType: CameraResultType.DataUrl,
    };

    const permission = await this.permissions.requestPermissions({
      permission: PermissionType.CAMERA,
      reason: permissionReason.CAMERA,
    });

    if (permission) {
      Camera.getPhoto(cameraOptions)
        .then((result: Photo) => {
          if (result.dataUrl !== undefined) {
            this.takePhoto.emit(
              this.fileRead.readImageDataIntoFile(result.dataUrl),
            );
          }
        })
        .catch(() => {
          this.capacitorCheckPermission();
        });
    }
  }

  private async capacitorCheckPermission(): Promise<void> {
    Camera.checkPermissions().then((result: PermissionStatus) => {
      if (result.camera === 'denied') {
        this.showError(MediaDeviceErrorCode.PERMISSION_DENIED);
      } else {
        this.toggleAccessDeniedBanner(false);
      }
    });
  }

  private toggleAccessDeniedBanner(val: boolean): void {
    if (val) {
      const commonToastOptions = {
        id: 'access-denied-toast',
        type: ToastAlertType.ERROR,
        closeable: true,
        indefinite: true,
      };

      let specificOptions: Partial<DynamicToastAlertOptions> = {};

      switch (this.mediaDeviceErrorCode) {
        case MediaDeviceErrorCode.FAILED_TO_LOAD:
          specificOptions.message = $localize`We couldn't access your camera. Please make sure that camera permission is enabled in your device settings.`;
          break;
        case MediaDeviceErrorCode.MEDIA_DEVICE_API_NOT_SUPPORTED:
          specificOptions.message = $localize`Oh no, we can't seem to recognize the camera. Please contact support`;
          break;
        case MediaDeviceErrorCode.UNSUPPORTED_BROWSER:
          specificOptions.message = $localize`Oops, you're using an unsupported browser. Please switch to a different browser or update to the latest version.`;
          break;
        case MediaDeviceErrorCode.PERMISSION_DENIED:
          specificOptions = {
            message: $localize`Please grant camera access in order to take photo.`,
            action: {
              type: 'button',
              text: $localize`Grant access`,
              trackingLabel: 'grantCameraAccessCTA',
              trackingSection: 'CameraInput',
              onClick: () => this.openApplicationSettings(),
            },
          };
          break;
        default:
          return;
      }

      this.toastAlertService.open({
        ...commonToastOptions,
        ...specificOptions,
      });
    } else {
      this.toastAlertService.close('access-denied-toast');
    }
  }

  openApplicationSettings(): void {
    this.nativeSettings.openAppSettings();
    this.toggleAccessDeniedBanner(false);
  }
}
